import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { apiUrl } from '../utils/api';
import { dataGridSx } from '../utils/dataGridStyles';
import {
  DataGrid,
  GridActionsCellItem,
  GridRowModes,
  GridColDef
} from '@mui/x-data-grid';
import {
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  Box,
  Badge,
  Tooltip,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DesignChangeDocuments from '../components/DesignChangeDocuments';

// Utility to display dates in dd.mm.yyyy format
const formatDate = (date: string | Date): string =>
  new Date(date).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

interface DesignChangesProps {
  projectId: number;
  access?: string;
  deleteAllAccess?: string;
  userId: number;
}

const DesignChanges: React.FC<DesignChangesProps> = ({ projectId, access = 'none', deleteAllAccess = 'none', userId }) => {
  const readOnly = access !== 'write';
  const canDeleteAll = deleteAllAccess === 'write';
  const [designChanges, setDesignChanges] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state for multiple column filters
  const [filterModel, setFilterModel] = useState({
    items: []
  });

  // “Add New” form state
  const [formData, setFormData] = useState({
    change_number: '',
    change_name: '',
    change_comments: '',
    change_date_issued: '',
    affected_systems: '',
    change_affecting_scope: true,
    asbuilt_included: false,
    change_status: 'Pending'
  });

  // Row edit mode state for DataGrid
  const [rowModesModel, setRowModesModel] = useState({});

  // individual column filters
  const [numFilter, setNumFilter] = useState([]);
  const [nameFilter, setNameFilter] = useState([]);
  const [commentsFilter, setCommentsFilter] = useState([]);
  const [systemFilter, setSystemFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState([]);
  const [scopeFilter, setScopeFilter] = useState([]);
  const [abFilter, setAbFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  const [totalFilter, setTotalFilter] = useState([]);
  const [pendingFilter, setPendingFilter] = useState([]);
  const [warningsOnly, setWarningsOnly] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedChangeNumber, setSelectedChangeNumber] = useState<string | null>(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingChangeNumber, setDeletingChangeNumber] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummaryData, setImportSummaryData] = useState<any>(null);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState<any>(null);

  useEffect(() => {
    if (!importing) return;
    const timer = setInterval(() => {
      setImportProgress(p => (p < 90 ? p + 5 : p));
    }, 300);
    return () => clearInterval(timer);
  }, [importing]);

  // refs and handlers for template import/export
  const fileInputRef = useRef(null);
  const [importSourceOpen, setImportSourceOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mapFile, setMapFile] = useState(null);
  const [fileHeaders, setFileHeaders] = useState([]);
  const mappingFields = [
    'change_number',
    'change_name',
    'change_comments',
    'affected_systems',
    'change_date_issued',
    'change_affecting_scope',
    'asbuilt_included',
    'change_status'
  ];
  const [mapping, setMapping] = useState(
    mappingFields.reduce((acc, f) => ({ ...acc, [f]: '' }), {})
  );
  const [dateFormat, setDateFormat] = useState('');

  useEffect(() => {
    if (!mapFile) { setFileHeaders([]); return; }
    const reader = new FileReader();
    reader.onload = e => {
      try {
        if (e.target?.result instanceof ArrayBuffer) {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const firstRow = (XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || []) as any[];
          const headers = firstRow.map(h => (h != null ? String(h) : ''));
          setFileHeaders(headers);
        }
      } catch (err) {
        console.error('Failed to read headers', err);
        setFileHeaders([]);
      }
    };
    reader.readAsArrayBuffer(mapFile);
  }, [mapFile]);


  const handleDownloadTemplate = () => {
    fetch(apiUrl(`/api/design-changes/template?project_id=${projectId}`))
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'design_changes_template.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert('Failed to download template'));
  };

  const sendImport = (file, confirm, overwrite = false) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(apiUrl(`/api/design-changes/import?confirm=${confirm}&project_id=${projectId}&overwrite=${overwrite}`), {
      method: 'POST',
      headers: { 'X-User-ID': String(userId) },
      body: fd
    }).then(res => res.json());
  };

  const processImportFile = file => {
    sendImport(file, false)
      .then(summary => {
        if (summary.error) {
          setImportPreviewData({ error: summary.error, file, total: 0, importable: 0, invalid_dates: [], invalid_numbers: [], invalid_status: [], duplicates: [], existing: [] });
          setImportPreviewOpen(true);
          return;
        }
        setImportPreviewData({ ...summary, file });
        setImportPreviewOpen(true);
      })
      .catch(() => {
        setImportPreviewData({ error: 'Import failed (server error)', file, total: 0, importable: 0, invalid_dates: [], invalid_numbers: [], invalid_status: [], duplicates: [], existing: [] });
        setImportPreviewOpen(true);
      });
  };

  const handlePreviewConfirm = (overwrite: boolean) => {
    const file = importPreviewData.file;
    setImportPreviewOpen(false);
    setImporting(true);
    setImportProgress(0);
    sendImport(file, true, overwrite).then(result => {
      setImportProgress(100);
      setTimeout(() => {
        setImporting(false);
        setImportProgress(0);
        const addedCount = result.created ?? 0;
        const overwrittenCount = result.updated ?? 0;
        const skippedCount = result.skipped ?? 0;
        const failedCount = result.errors ?? 0;
        setImportSummaryData({ added: addedCount, overwritten: overwrittenCount, skipped: skippedCount, failed: failedCount, total: result.total_processed ?? importPreviewData.total, preview: importPreviewData, overwrite, file, rowStatuses: result.row_statuses ?? {} });
        setImportSummaryOpen(true);
        fetchData();
      }, 500);
    }).catch(err => {
      setImporting(false);
      setImportProgress(0);
      setImportPreviewData({ error: err?.message || 'Import failed (server error)', file, total: 0, importable: 0, invalid_dates: [], invalid_numbers: [], invalid_status: [], duplicates: [], existing: [] });
      setImportPreviewOpen(true);
    });
  };

  const downloadPreviewLog = () => {
    if (!importPreviewData) return;
    downloadImportLog({ preview: importPreviewData, overwrite: false, file: importPreviewData.file });
  };

  const handleFileChange = e => {
    if (readOnly) return;
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    processImportFile(file);
  };

  const handleImportClick = () => {
    if (readOnly) return;
    setImportSourceOpen(true);
  };

  const handleImportSource = isThird => {
    setImportSourceOpen(false);
    if (isThird) {
      setMappingOpen(true);
    } else {
      fileInputRef.current && fileInputRef.current.click();
    }
  };

  const handleMappingChange = field => (_, value) => {
    setMapping(prev => ({ ...prev, [field]: value || '' }));
  };

  const mapRowsToWorkbook = rows => {
    const data = [
      ['Number','Name','Comments','Affected Systems','Date Issued','Affecting','AB Y/N','Status'],
      ...rows.map(r => [
        r.change_number || '',
        r.change_name || '',
        r.change_comments || '',
        r.affected_systems || '',
        r.change_date_issued || '',
        r.change_affecting_scope || '',
        r.asbuilt_included || '',
        r.change_status || ''
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new File([wbout], 'mapped.xlsx', {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  };

  const handleMappingSubmit = () => {
    if (!mapFile) { alert('Select a file'); return; }
    const fd = new FormData();
    fd.append('file', mapFile);
    fd.append('mapping', JSON.stringify(mapping));
    if (dateFormat) fd.append('date_format', dateFormat);
    fetch(apiUrl('/api/design-changes/export-map'), { method: 'POST', body: fd })
      .then(res => res.json())
      .then(rows => {
        const file = mapRowsToWorkbook(rows);
        setMappingOpen(false);
        setMapFile(null);
        processImportFile(file);
      })
      .catch(() => alert('Failed to map file'));
  };

  const fetchData = useCallback(() => {
    if (access === 'none') { setDesignChanges([]); return; }
    if (!projectId) { setDesignChanges([]); return; }
    setLoading(true);
    Promise.all([
      fetch(apiUrl(`/api/design-changes?project_id=${projectId}`)).then(res => res.json()),
      fetch(apiUrl(`/api/assignments?project_id=${projectId}`)).then(res => res.json())
    ])
      .then(([changes, assignments]) => {
        const rows = changes.map(c => {
          const related = assignments.filter(a => a.change_number === c.change_number);
          const totalDoc = related.filter(a => ['Pending', 'Submitted'].includes(a.assignment_document_status)).length;
          const pendingDoc = related.filter(a => a.assignment_document_status === 'Pending').length;
          return {
            id: c.change_number,
            total_doc: totalDoc,
            pending_doc: pendingDoc,
            ...c
          };
        });
        setDesignChanges(rows);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [access, projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleAddChange = () => {
    if (readOnly) return;
    setFormError(null);
    setAddLoading(true);
    fetch(apiUrl('/api/design-changes'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...formData, project_id: projectId })
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to add design change');
        return data;
      })
      .then(() => {
        fetchData();
        window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        setFormError(null);
        setFormData({
          change_number: '',
          change_name: '',
          change_comments: '',
          change_date_issued: '',
          affected_systems: '',
          change_affecting_scope: true,
          asbuilt_included: false,
          change_status: 'Pending'
        });
        setAddOpen(false); // Close dialog on success
      })
      .catch(err => setFormError(err.message))
      .finally(() => setAddLoading(false));
  };

  const handleDeleteClick = useCallback((id: string) => () => {
    if (readOnly) return;
    setDeletingChangeNumber(id);
    setDeleteDialogOpen(true);
  }, [readOnly]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingChangeNumber) return;

    setDeleteLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/design-changes/${deletingChangeNumber}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Delete failed');
      await fetchData();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
      setDeleteDialogOpen(false);
      setDeletingChangeNumber(null);
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  }, [deletingChangeNumber, fetchData]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeletingChangeNumber(null);
  }, []);

  const handleEditClick = id => () => {
    if (readOnly) return;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = id => () => {
    if (readOnly) return;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = id => () => {
    if (readOnly) return;
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });
  };

  const processRowUpdate = updatedRow => {
    const {
      id,
      change_name,
      change_comments,
      change_date_issued,
      affected_systems,
      change_affecting_scope,
      change_status,
      asbuilt_included
    } = updatedRow;
    const isoDate =
      change_date_issued && typeof change_date_issued !== 'string'
        ? new Date(change_date_issued).toISOString().slice(0, 10)
        : change_date_issued;
    return fetch(apiUrl(`/api/design-changes/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        change_name,
        change_comments,
        change_date_issued: isoDate,
        affected_systems,
        change_affecting_scope,
        change_status,
        asbuilt_included
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Update failed');
        return res.json();
      })
      .then(() => {
        fetchData();
        window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        return updatedRow;
      })
      .catch(err => {
        alert(err.message);
        throw err;
      });
  };

  const warningColorClass = 'warning-cell';

  const getStatusRowClass = row => {
    if (row.change_status === 'NA') return 'status-na-row';
    if (row.change_status === 'Completed') return 'status-completed-row';
    return '';
  };

  const isScopeNaWarning = row => {
    return row.change_affecting_scope && row.change_status === 'NA';
  };

  const isStatusWarning = row => {
    if (!row.change_affecting_scope && row.change_status !== 'NA') return true;
    if (isScopeNaWarning(row)) return true;
    if (row.change_status === 'Pending' && row.pending_doc === 0) return true;
    if (row.total_doc > 0 && row.pending_doc === 0 && row.change_status !== 'Completed') return true;
    if (row.total_doc === 0 && row.change_status === 'Pending') return true;
    if (row.total_doc === 0 && row.change_status === 'Completed') return true;
    if (row.total_doc > 0 && row.change_status === 'NA') return true;
    if (row.change_status === 'Completed' && row.pending_doc > 0) return true;
    return false;
  };

  const isTotalDwgWarning = row => {
    if (row.total_doc === 0 && row.change_status === 'Pending') return true;
    if (row.total_doc === 0 && row.change_status === 'Completed') return true;
    return false;
  };

  const isPendingDwgWarning = row => {
    if (row.change_status === 'Completed' && row.pending_doc > 0) return true;
    if (row.change_status === 'Pending' && row.pending_doc === 0) return true;
    return false;
  };

  const hasAnyWarning = row =>
    isScopeNaWarning(row) ||
    isStatusWarning(row) ||
    isTotalDwgWarning(row) ||
    isPendingDwgWarning(row);

  // helper for filtering
  const passes = (row, exclude) => {
    if (exclude !== 'change_number' && numFilter.length && !numFilter.includes(row.change_number)) return false;
    if (exclude !== 'change_name' && nameFilter.length && !nameFilter.includes(row.change_name)) return false;
    if (exclude !== 'change_comments' && commentsFilter.length && !commentsFilter.includes(row.change_comments)) return false;
    if (exclude !== 'affected_systems' && systemFilter.length && !systemFilter.includes(row.affected_systems)) return false;
    if (exclude !== 'change_date_issued' && dateFilter.length && !dateFilter.includes(row.change_date_issued)) return false;
    const scopeVal = row.change_affecting_scope ? 'Yes' : 'No';
    if (exclude !== 'change_affecting_scope' && scopeFilter.length && !scopeFilter.includes(scopeVal)) return false;
    const abVal = row.asbuilt_included ? 'Yes' : 'No';
    if (exclude !== 'asbuilt_included' && abFilter.length && !abFilter.includes(abVal)) return false;
    if (exclude !== 'change_status' && statusFilter.length && !statusFilter.includes(row.change_status)) return false;
    const totalVal = String(row.total_doc);
    if (exclude !== 'total_doc' && totalFilter.length && !totalFilter.includes(totalVal)) return false;
    const pendVal = String(row.pending_doc);
    if (exclude !== 'pending_doc' && pendingFilter.length && !pendingFilter.includes(pendVal)) return false;
    return true;
  };

  const unique = (rows, field) =>
    Array.from(new Set(rows.map(r => r[field]).filter(v => v !== undefined && v !== null))).map(v => String(v));

  const numberOptions = unique(designChanges.filter(r => passes(r, 'change_number')), 'change_number');
  const nameOptions = unique(designChanges.filter(r => passes(r, 'change_name')), 'change_name');
  const commentsOptions = unique(designChanges.filter(r => passes(r, 'change_comments')), 'change_comments');
  const systemOptions = unique(designChanges.filter(r => passes(r, 'affected_systems')), 'affected_systems');
  const dateOptions = unique(designChanges.filter(r => passes(r, 'change_date_issued')), 'change_date_issued');
  const scopeOptions = Array.from(
    new Set(
      designChanges
        .filter(r => passes(r, 'change_affecting_scope'))
        .map(r => (r.change_affecting_scope ? 'Yes' : 'No'))
    )
  );
  const abOptions = Array.from(
    new Set(
      designChanges
        .filter(r => passes(r, 'asbuilt_included'))
        .map(r => (r.asbuilt_included ? 'Yes' : 'No'))
    )
  );
  const statusOptions = unique(designChanges.filter(r => passes(r, 'change_status')), 'change_status');
  const totalOptions = unique(designChanges.filter(r => passes(r, 'total_doc')), 'total_doc');
  const pendingOptions = unique(designChanges.filter(r => passes(r, 'pending_doc')), 'pending_doc');

  const filteredDesignChanges = designChanges.filter(
    r => passes(r, undefined) && (!warningsOnly || hasAnyWarning(r))
  );

  const warningsCount = designChanges.filter(
    r => passes(r, undefined) && hasAnyWarning(r)
  ).length;

const columns: GridColDef[] = [
    { field: 'change_number', headerName: 'Number', flex: 1.2 },
    { field: 'change_name', headerName: 'Name', flex: 2, editable: !readOnly },
    { field: 'change_comments', headerName: 'Comments', flex: 3, editable: !readOnly },
    { field: 'affected_systems', headerName: 'Affected Systems', flex: 2, editable: !readOnly },
    {
      field: 'change_date_issued',
      headerName: 'Date Issued',
      flex: 1.2,
      editable: !readOnly,
      renderCell: (params) => (params.value ? formatDate(params.value) : ''),
      renderEditCell: (params) => (
        <TextField
          type="date"
          size="small"
          value={params.value ? String(params.value).slice(0, 10) : ''}
          onChange={(e) =>
            params.api.setEditCellValue(
              { id: params.id, field: params.field, value: e.target.value },
              e
            )
          }
          InputLabelProps={{ shrink: true }}
        />
      )
    },
    {
      field: 'change_affecting_scope',
      headerName: 'Affecting Scope',
      type: 'boolean',
      flex: 1,
      editable: !readOnly,
      //       cellClassName: params =>
      //         isScopeNaWarning(params.row) ? warningColorClass : ''
    },
    {
      field: 'asbuilt_included',
      headerName: 'AB Y/N',
      type: 'boolean',
      flex: 1,
      editable: !readOnly
    },
    {
      field: 'change_status',
      headerName: 'Status',
      flex: 1,
      editable: !readOnly,
      type: 'singleSelect',
      valueOptions: ['NA', 'Pending', 'Completed'],
      //       cellClassName: params =>
      //         isStatusWarning(params.row) ? warningColorClass : ''
    },
    {
      field: 'qs_status',
      headerName: 'QS Status',
      flex: 1,
      editable: false,
      renderCell: (params) => {
        const statusColors = { 'Pending': 'warning', 'Submitted': 'info', 'Approved': 'success' };
        return (
          <Chip
            label={params.value || 'Pending'}
            size="small"
            color={statusColors[params.value] || 'default'}
            onClick={(e) => e.stopPropagation()}
          />
        );
      }
    },
    {
      field: 'total_doc',
      headerName: 'Total DOC',
      flex: 1,
      sortable: true,
      filterable: true,
      //       cellClassName: params =>
      //         isTotalDwgWarning(params.row) ? warningColorClass : ''
    },
    {
      field: 'pending_doc',
      headerName: 'Pending DOC',
      flex: 1,
      sortable: true,
      filterable: true,
      //       cellClassName: params =>
      //         isPendingDwgWarning(params.row) ? warningColorClass : ''
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 1.5,
      getActions: ({ row }) => {
        const id = row.id;
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isEditing && !readOnly) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
              label="Save"
              onClick={handleSaveClick(id)}
              showInMenu={false}
              key="save"
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
              label="Cancel"
              onClick={handleCancelClick(id)}
              showInMenu={false}
              key="cancel"
            />,
          ];
        }

        const count = row.document_count || 0;

        const actions = [
          <GridActionsCellItem
            icon={
              <Tooltip title="Documents">
                <Badge badgeContent={count} color={count > 0 ? 'primary' : 'default'} max={99}>
                  <AttachFileIcon />
                </Badge>
              </Tooltip>
            }
            label="Documents"
            onClick={() => {
              setSelectedChangeNumber(id);
              setDocumentsDialogOpen(true);
            }}
            showInMenu={false}
            key="documents"
          />,
        ];

        if (!readOnly) {
          actions.push(
            <GridActionsCellItem
              icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
              label="Edit"
              onClick={handleEditClick(id)}
              showInMenu={false}
              key="edit"
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Delete"><DeleteIcon color="error" /></Tooltip>}
              label="Delete"
              onClick={handleDeleteClick(id)}
              showInMenu={false}
              key="delete"
            />,
          );
        }

        return actions;
      }
    }
  ];

  const rowsToWorkbook = rows => {
    const data = [
      [
        'Number',
        'Name',
        'Comments',
        'Affected Systems',
        'Date Issued',
        'Affecting',
        'AB Y/N',
        'Status'
      ],
      ...rows.map(r => [
        r.change_number || '',
        r.change_name || '',
        r.change_comments || '',
        r.affected_systems || '',
        r.change_date_issued ? formatDate(r.change_date_issued) : '',
        r.change_affecting_scope ? 'Yes' : 'No',
        r.asbuilt_included ? 'Yes' : 'No',
        r.change_status || ''
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new File([wbout], 'design_changes.xlsx', {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  };

  const handleExport = () => {
    const rows = filteredDesignChanges;
    const file = rowsToWorkbook(rows);
    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'design_changes.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadImportLog = (summaryData: any) => {
    const { preview, overwrite, file, rowStatuses = {} } = summaryData;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
        const globalError = preview.error || null;
        const failedMap: Record<number, string[]> = {};
        const invalidDateSet = new Set(preview.invalid_dates || []);
        const invalidNumSet = new Set(preview.invalid_numbers || []);
        const invalidStatusSet = new Set(preview.invalid_status || []);
        const dupRowSet = new Set((preview.duplicates || []).map((d: any) => d.row));
        const logRows: { row_no: number; change_number: string; status: string; notes: string }[] = [];
        rows.slice(1).forEach((row, i) => {
          const rowIdx = i + 2;
          const changeNum = row[0] != null ? String(row[0]).trim() : '';
          if (!changeNum) return;
          if (globalError) {
            logRows.push({ row_no: rowIdx, change_number: changeNum, status: 'Failed', notes: globalError });
            return;
          }
          const errorReasons: string[] = [];
          if (invalidNumSet.has(rowIdx)) errorReasons.push('Missing/invalid change number');
          if (invalidDateSet.has(rowIdx)) errorReasons.push('Invalid date format');
          if (invalidStatusSet.has(rowIdx)) errorReasons.push('Invalid status');
          if (dupRowSet.has(rowIdx)) errorReasons.push('Duplicate number in file');
          const backendStatus = rowStatuses[rowIdx];
          let status: string;
          let notes: string;
          if (errorReasons.length > 0) {
            status = 'Failed';
            notes = errorReasons.join('; ');
          } else if (backendStatus === 'Skipped') {
            status = 'Pass';
            notes = 'Existing kept (skipped)';
          } else if (backendStatus === 'Overwritten') {
            status = 'Pass';
            notes = 'Item overwritten';
          } else if (backendStatus === 'Added') {
            status = 'Pass';
            notes = 'New Item added';
          } else {
            status = 'Pass';
            notes = 'New Item added';
          }
          logRows.push({ row_no: rowIdx, change_number: changeNum, status, notes });
        });
        fetch(apiUrl('/api/design-changes/import-log'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ rows: logRows })
        })
          .then(res => res.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'import_log.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
          })
          .catch(() => alert('Failed to generate log file'));
      } catch {
        alert('Failed to generate log file');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDeleteAll = () => {
    if (!canDeleteAll) return;
    if (!window.confirm('Delete ALL design changes?')) return;
    fetch(apiUrl(`/api/design-changes/delete-all?project_id=${projectId}`), {
      method: 'DELETE',
      headers: { 'X-User-ID': String(userId) }
    })
      .then(res => {
        if (!res.ok) throw new Error('Delete all failed');
        fetchData();
      })
      .catch(err => alert(err.message));
  };

  if (access === 'none') {
    return <div>Access denied</div>;
  }

  if (!projectId) {
    return <div>Please select a project.</div>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', margin: '0 24px 16px 24px' }}>
        {!readOnly && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add Design Change
          </Button>
        )}
      </Box>


      {/* Filters aligned with headers */}
      <Accordion expanded={filtersOpen} onChange={() => setFiltersOpen(o => !o)} sx={{ mb: 2, mx: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Design Change Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 2fr 3fr 2fr 1.2fr 1fr 1fr 1fr 1fr 1fr 1.2fr',
              gap: 1,
              mb: 1
            }}
          >
        <Autocomplete
          multiple
          size="small"
          options={numberOptions}
          value={numFilter}
          onChange={(_, v) => setNumFilter(v)}
          renderInput={p => (
            <TextField {...p} label="Number" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          options={nameOptions}
          value={nameFilter}
          onChange={(_, v) => setNameFilter(v)}
          renderInput={p => (
            <TextField {...p} label="Name" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          options={commentsOptions}
          value={commentsFilter}
          onChange={(_, v) => setCommentsFilter(v)}
          renderInput={p => (
            <TextField {...p} label="Comments" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          options={systemOptions}
          value={systemFilter}
          onChange={(_, v) => setSystemFilter(v)}
          renderInput={p => (
            <TextField {...p} label="Systems" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          options={dateOptions}
          value={dateFilter}
          onChange={(_, v) => setDateFilter(v)}
          renderInput={p => (
            <TextField {...p} label="Date Issued" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          options={scopeOptions}
          value={scopeFilter}
          onChange={(_, v) => setScopeFilter(v)}
          renderInput={p => (
            <TextField {...p} label="Affecting" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          options={abOptions}
          value={abFilter}
          onChange={(_, v) => setAbFilter(v)}
          renderInput={p => (
            <TextField {...p} label="AB Y/N" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          options={statusOptions}
          value={statusFilter}
          onChange={(_, v) => setStatusFilter(v)}
          renderInput={p => (
            <TextField {...p} label="Status" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          options={totalOptions}
          value={totalFilter}
          onChange={(_, v) => setTotalFilter(v)}
          renderInput={p => (
            <TextField {...p} label="Total DOC" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          options={pendingOptions}
          value={pendingFilter}
          onChange={(_, v) => setPendingFilter(v)}
          renderInput={p => (
            <TextField {...p} label="Pending DOC" InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
          )}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            startIcon={<WarningIcon />}
            variant={warningsOnly ? 'contained' : 'outlined'}
            onClick={() => setWarningsOnly(w => !w)}
            sx={{ fontSize: '0.75rem', minWidth: '40px' }}
          >
            {warningsCount}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setNumFilter([]);
              setNameFilter([]);
              setCommentsFilter([]);
              setSystemFilter([]);
              setDateFilter([]);
              setScopeFilter([]);
              setAbFilter([]);
              setStatusFilter([]);
              setTotalFilter([]);
              setPendingFilter([]);
              setWarningsOnly(false);
            }}
            sx={{ fontSize: '0.75rem', minWidth: '40px' }}
          >
            <FilterAltOffIcon />
          </Button>
        </Box>
        </Box>
        </AccordionDetails>
      </Accordion>

      {/* DataGrid with multi-column filters */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', background: 'white', margin: '0 24px' }}>
        <DataGrid
          rows={filteredDesignChanges}
          columns={columns}
          loading={loading}
          autoHeight
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          filterMode="client"
          filterModel={filterModel}
          onFilterModelChange={newModel => setFilterModel(newModel)}
          sortingMode="client"
          editMode={readOnly ? undefined : 'row'}
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          processRowUpdate={readOnly ? undefined : processRowUpdate}
          onRowDoubleClick={params => !readOnly && handleEditClick(params.id)()}
          sx={dataGridSx}
        />
      </Box>

      <Box sx={{ margin: '16px 24px', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handleDownloadTemplate}>
          Export Template
        </Button>
        <Button variant="contained" onClick={handleExport}>
          Export To Excel
        </Button>
        <Button variant="contained" onClick={handleImportClick} disabled={readOnly}>
          Import From Excel
        </Button>
        {canDeleteAll && (
          <Button variant="contained" color="error" onClick={handleDeleteAll}>
            Delete All
          </Button>
        )}
        <input
          type="file"
          accept=".xlsx"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Dialogs */}
      {/* Add Design Change Dialog */}
      <Dialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setFormError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Add New Design Change
          </Typography>
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              name="change_number"
              label="Change Number"
              fullWidth
              value={formData.change_number}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="change_name"
              label="Name"
              fullWidth
              value={formData.change_name}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="change_comments"
              label="Comments"
              fullWidth
              multiline
              rows={3}
              value={formData.change_comments}
              onChange={handleInputChange}
            />
            <TextField
              name="affected_systems"
              label="Affected Systems"
              fullWidth
              value={formData.affected_systems}
              onChange={handleInputChange}
            />
            <TextField
              name="change_date_issued"
              label="Date Issued"
              type="date"
              fullWidth
              value={formData.change_date_issued}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              name="change_status"
              label="Status"
              select
              fullWidth
              value={formData.change_status}
              onChange={handleInputChange}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.change_affecting_scope}
                    onChange={handleInputChange}
                    name="change_affecting_scope"
                  />
                }
                label="Affecting Scope"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.asbuilt_included}
                    onChange={handleInputChange}
                    name="asbuilt_included"
                  />
                }
                label="As-Built Included"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setAddOpen(false);
              setFormError(null);
            }}
            disabled={addLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddChange}
            disabled={addLoading}
            startIcon={addLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {addLoading ? 'Adding...' : 'Add Design Change'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importing}>
        <DialogTitle>Importing...</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <LinearProgress variant="determinate" value={importProgress} />
          <Typography sx={{ mt: 1 }} align="center">{importProgress}%</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
          <DialogTitle>Import Source</DialogTitle>
          <DialogContent>
            <Typography>Is this Excel file from a third-party source?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleImportSource(false)}>Template</Button>
            <Button variant="contained" onClick={() => handleImportSource(true)}>Third Party</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={mappingOpen} onClose={() => setMappingOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Column Mapping</DialogTitle>
          <DialogContent>
            <input type="file" accept=".xlsx" onChange={e => setMapFile(e.target.files[0])} style={{ marginBottom: 16 }} />
            {mappingFields.map(field => (
              <TextField
                key={field}
                select
                label={field.replace(/_/g, ' ')}
                value={mapping[field]}
                onChange={e => handleMappingChange(field)(null, e.target.value)}
                fullWidth
                margin="dense"
              >
                {fileHeaders.map(h => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </TextField>
            ))}
            <TextField
              select
              label="Date Format"
              value={dateFormat}
              onChange={e => setDateFormat(e.target.value)}
              fullWidth
              margin="dense"
            >
              {['dd.mm.yyyy','mm/dd/yyyy','yyyy-mm-dd','dd/mm/yyyy'].map(fmt => (
                <MenuItem key={fmt} value={fmt}>{fmt}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleMappingSubmit}>Import</Button>
          </DialogActions>
        </Dialog>

        {/* Import Preview Dialog */}
        <Dialog open={importPreviewOpen} onClose={() => setImportPreviewOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{importPreviewData?.error ? 'Import Failed' : 'Import Preview'}</DialogTitle>
          <DialogContent>
            {importPreviewData && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {importPreviewData.error && (
                  <Box sx={{ p: 1.5, bgcolor: 'error.light', borderRadius: 1 }}>
                    <Typography color="error.contrastText" fontWeight={600}>{importPreviewData.error}</Typography>
                  </Box>
                )}
                {!importPreviewData.error && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Total rows in file:</Typography>
                  <Typography fontWeight={600}>{importPreviewData.total}</Typography>
                </Box>}
                {importPreviewData.invalid_dates?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Invalid dates:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.invalid_dates.length}</Typography>
                  </Box>
                )}
                {importPreviewData.invalid_numbers?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Missing number:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.invalid_numbers.length}</Typography>
                  </Box>
                )}
                {importPreviewData.invalid_status?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Invalid status:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.invalid_status.length}</Typography>
                  </Box>
                )}
                {importPreviewData.duplicates?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Duplicates in file:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.duplicates.length}</Typography>
                  </Box>
                )}
                {importPreviewData.existing?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="warning.main">Already exist:</Typography>
                    <Typography fontWeight={600} color="warning.main">{importPreviewData.existing.length}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', pt: 1 }}>
                  <Typography fontWeight={600}>Rows to import:</Typography>
                  <Typography fontWeight={600}>{importPreviewData.importable}</Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportPreviewOpen(false)}>Cancel</Button>
            {importPreviewData?.existing?.length > 0 ? (
              <>
                <Button variant="outlined" onClick={() => handlePreviewConfirm(false)}>Import (Skip Existing)</Button>
                <Button variant="outlined" onClick={() => handlePreviewConfirm(true)}>Import (Overwrite)</Button>
              </>
            ) : (
              <Button variant="contained" onClick={() => handlePreviewConfirm(false)} disabled={!importPreviewData || importPreviewData.importable === 0}>
                Proceed with Import
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Import Summary Dialog */}
        <Dialog open={importSummaryOpen} onClose={() => setImportSummaryOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Import Complete</DialogTitle>
          <DialogContent>
            {importSummaryData && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Total rows in file:</Typography>
                  <Typography fontWeight={600}>{importSummaryData.total}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="success.main">New items added:</Typography>
                  <Typography fontWeight={600} color="success.main">{importSummaryData.added}</Typography>
                </Box>
                {importSummaryData.overwritten > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="warning.main">Items overwritten:</Typography>
                    <Typography fontWeight={600} color="warning.main">{importSummaryData.overwritten}</Typography>
                  </Box>
                )}
                {importSummaryData.skipped > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Existing kept (skipped):</Typography>
                    <Typography fontWeight={600} color="text.secondary">{importSummaryData.skipped}</Typography>
                  </Box>
                )}
                {importSummaryData.failed > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Failed rows:</Typography>
                    <Typography fontWeight={600} color="error.main">{importSummaryData.failed}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', pt: 1 }}>
                  <Typography fontWeight={600}>Total changes:</Typography>
                  <Typography fontWeight={600}>{importSummaryData.added + importSummaryData.overwritten}</Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { downloadImportLog(importSummaryData); }}>Download Import Log</Button>
            <Button variant="contained" onClick={() => setImportSummaryOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Documents Dialog */}
        <Dialog
          open={documentsDialogOpen}
          onClose={() => setDocumentsDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Design Change Documents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedChangeNumber}
              </Typography>
            </Box>
            <IconButton onClick={() => setDocumentsDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selectedChangeNumber && (
              <DesignChangeDocuments
                changeNumber={selectedChangeNumber}
                projectId={projectId}
                canWrite={!readOnly}
                onDocumentCountChange={() => fetchData()}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon color="error" />
              <Typography variant="h6" fontWeight={600} color="error.main">
                Confirm Delete
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Are you sure you want to delete this design change?
            </Typography>
            <Box
              sx={{
                bgcolor: 'grey.100',
                borderRadius: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Change Number
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {deletingChangeNumber}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This will also delete all related documents, assignments, and BOQ lines. This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleteLoading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={deleteLoading}
              startIcon={deleteLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default DesignChanges;